<script setup>
import { ref, watch, onMounted, computed, inject } from 'vue'
import { useStore } from 'vuex'
import { CloudUploadOutlined } from '@ant-design/icons-vue'
import { getNeed, getAuthCode, checkCode } from '@/api/upload.js'
import { message } from 'ant-design-vue'
import { getProportion, getDataSource, drawRect } from '@/utils/coord.js'
import { useRoute } from 'vue-router'
import { application } from './contant.js'
import Indicator from '@/components/Indicator.vue'
import EmptyItem from './empty-item.vue'
import RightDec from './right-dec.vue'
import CropText from './crop-text.vue'
import Tips from './tips.vue'
import { setTrack } from '@/api/login'
import IntelligentModule from '@/components/IntelligentModule.vue'
import Application from './application1'
import commonUrl from '@/views/recognition_general/interface.js'
import { getAlgorithmProcessor } from './processors.js'
import { resolveApiAssetUrl } from '@/utils/algorithmPublicAssets'
import { hasLoginSession, requireLoginSession } from '@/utils/experience'
import { useRecognitionImages } from '@/utils/useRecognitionImages'

const { loadRecognitionImages, resetRecognitionImages } = useRecognitionImages()

const visible = ref(false)
const value = ref('')
const errorShow = ref(false)
const nullShow = ref(false)
const loading = ref(false)
const activeImage = ref(null)
const selectedFile = ref(null)
const isNeedVerify = ref(false)
const errorMsg = ref('')
const formatCheck = ref(false)
//路由
import { getRouteName } from '@/views/algorithm-detail/utils.js'

const route = useRoute()
const routeName = getRouteName(route)
const detailData = inject('detailData', null)
const algorithmType = computed(
  () => detailData.value?.algorithm_type?.type || '',
)
const routeSource = computed(() => algorithmType.value)
const exampleImages = computed(() =>
  (detailData.value?.demo?.example_images || []).map(resolveApiAssetUrl),
)
const exampleCut = computed(() => {
  const cut = detailData.value?.demo?.example_cut || {}
  const result = {}
  Object.keys(cut).forEach((key) => {
    result[key] = (cut[key] || []).map((item) => ({
      ...item,
      url: resolveApiAssetUrl(item.url),
    }))
  })
  return result
})

const curData = computed(() => detailData.value || {})

let captcha = ref({})
const empty = ref(false)
const canvasShow = ref(false)
const canvasW = ref(0)
const canvasH = ref(0)
const canvasRef = ref(null)
const originalImg = ref('')
const originalShow = ref(false)
const sizeCheck = ref(false)
const result = ref({})

const fileInput = ref(null)
const imagePreview = ref(null)
// 右侧小图选中
const active = ref(-2)
const selectItem = ref({})
//右侧小图列表
const cropList = ref([])
const often = ref(false)
const callable = ref(false)
const serverBusy = ref(false)

const OcrType = [
  'ocrBusinessLicense',
  'ocrBankCard',
  'ocrInvoiceInformation',
  'ocrDriverLicense',
  'classificationGeneral',
  'classificationAnimal',
  'classificationVegetables',
  'classificationVehicleType',
  'classificationDishes',
  'classificationLandmark',
  'ocrWaterMeter',
].includes(routeName)

const source = ref('face')
onMounted(async () => {
  activeImage.value = exampleImages.value[0] || null
  handleActive(activeImage.value, 0)
  source.value = routeSource.value.split('_')[0]
  getVerify()
})

watch(
  visible,
  (newValue) => {
    if (newValue) {
      getCode()
    }
  },
  { immediate: true },
)

const handleChange = (e) => {
  const data = e.target.value
  if (!data) {
    nullShow.value = false
  }
}

const track = async () => {
  await setTrack({
    position: `algorithm_market > ${routeSource.value} > task`,
  })
}

const handleConfirm = async () => {
  if (!value.value) {
    nullShow.value = true
    return
  }
  const data = {
    captcha_key: captcha.value.captcha_key,
    captcha_value: value.value,
  }
  try {
    let res = await checkCode(data)
    errorShow.value = !res.status
    errorMsg.value = res.message
    if (res.status) {
      isNeedVerify.value = false
      visible.value = false
      triggerFileInput()
    } else {
      getCode()
    }
  } catch (err) {
    console.log('err', err)
  }
}

const handleActive = (item, idx) => {
  resetRecognitionImages()
  activeImage.value = item
  result.value = {}
  selectItem.value = {}
  if (item) empty.value = false

  originalShow.value = false
  const cut = exampleCut.value[idx + 1]
  if (cut) {
    cropList.value = cut
    if (OcrType) {
      selectItem.value = cut[0]
    }

    // 如果 cropList 有数据，默认选中第一项（有label选第二项，否则选第一项）
    if (cut.length > 1 && cut[1].label !== undefined) {
      active.value = 1
      selectItem.value = cut[1]
    } else if (cut.length > 0) {
      active.value = 0
      selectItem.value = cut[0]
    }
  }
  const image = document.getElementById('previewImg')
  const ctx = canvasRef.value.getContext('2d')

  image.onload = function (event) {
    const size = getProportion(
      event.target.naturalWidth,
      event.target.naturalHeight,
      585,
      370,
      585,
      370,
    )

    canvasRef.value.width = size.width
    canvasRef.value.height = size.height

    ctx.drawImage(image, 0, 0, Math.ceil(size.width), Math.ceil(size.height))
    if (active.value === -1) {
      const shapes = result.value.shapes || []
      shapes.forEach((i) => {
        if (i.points.length) {
          const _data = getDataSource(
            i,
            result.value.imageWidth,
            result.value.imageHeight,
            canvasRef.value.width,
            canvasRef.value.height,
          )
          drawRect(ctx, {
            ...i,
            points: _data,
          })
        }
      })
      if (
        shapes.length &&
        routeSource.value !== 'image_tech_super_resolution'
      ) {
        originalShow.value = true
      }
    }
  }
}

const triggerFileInput = async () => {
  if (!requireLoginSession()) return
  try {
    let res = await getNeed()
    isNeedVerify.value = res.need
    callable.value = res.callable
    if (!res.callable) {
      message.warning('今天的试用次数已用完，请联系咨询', 4)
      return
    }
    if (!res.need) {
      fileInput.value.click()
    } else {
      visible.value = true
      value.value = ''
    }
  } catch (error) {
    console.log(error)
    visible.value = true
    value.value = ''
  }
}

const convertBytesToMB = (bytes) => {
  return bytes / (1024 * 1024) // 将字节转换为MB
}

const handleFileChange = (event) => {
  if (!requireLoginSession()) {
    event.target.value = ''
    return
  }
  if (loading.value) {
    message.warning('已在上传中，请稍候')
    return
  }
  const file = event.target.files[0] // 获取第一个文件，即用户选择的文件
  if (file) {
    formatCheck.value = false
    sizeCheck.value = false
    serverBusy.value = false
    often.value = false
    if (
      !['image/jpeg', 'image/png', 'image/jpg', 'image/bmp'].includes(file.type)
    ) {
      formatCheck.value = true
      activeImage.value = ''
      empty.value = true
      const el = canvasRef.value.getContext('2d')
      fileInput.value.value = ''
      el.clearRect(0, 0, canvasRef.value.width, canvasRef.value.height)
      return
    }
    if (+convertBytesToMB(file.size).toFixed() > 2) {
      sizeCheck.value = true
      activeImage.value = ''
      empty.value = true
      fileInput.value.value = ''
      const el = canvasRef.value.getContext('2d')
      el.clearRect(0, 0, canvasRef.value.width, canvasRef.value.height)
      return
    }
    selectedFile.value = file
    previewImage(file)
  }
}

const showLeftImage = (item, id) => {
  active.value = id
  selectItem.value = item
  activeImage.value = item.url
  if (id === -1) {
    drawImage(result.value)
  } else {
    activeImage.value = item.url
  }
}

const drawImage = async (res, crop = false) => {
  const el = canvasRef.value.getContext('2d')
  el.clearRect(0, 0, canvasRef.value.width, canvasRef.value.height)
  const image = document.getElementById('previewImg')
  const size = getProportion(
    res.imageWidth,
    res.imageHeight,
    585,
    370,
    585,
    370,
  )
  canvasRef.value.width = size.width
  canvasRef.value.height = size.height
  image.width = size.width
  image.height = size.height
  el.drawImage(image, 0, 0, canvasRef.value.width, canvasRef.value.height)
  const shapes = res.shapes || []
  shapes.forEach((i) => {
    if (shapes.length && routeSource.value !== 'image_tech_super_resolution')
      originalShow.value = true
    if (i.points.length) {
      const _data = getDataSource(
        i,
        res.imageWidth,
        res.imageHeight,
        canvasRef.value.width,
        canvasRef.value.height,
      )
      drawRect(el, {
        ...i,
        points: _data,
      })
    }
  })
  if (crop) {
    const images = await loadRecognitionImages(shapes, route.query.id || detailData.value?.id)
    if (!images) return
    cropList.value = images
  }
  //营业执照显示文字
  if (OcrType) {
    selectItem.value = cropList.value[0]
  }
}

const interfaceType = (data) => {
  const processor = getAlgorithmProcessor(algorithmType.value)
  if (processor) {
    return processor.apiMethod(data)
  }
  throw new Error(`Unsupported algorithm_type: ${algorithmType.value}`)
}

const previewImage = async (file) => {
  if (file) {
    resetRecognitionImages()
    const reader = new FileReader()
    reader.onload = (e) => {
      activeImage.value = e.target.result // 更新图片预览的DataURL
      originalImg.value = e.target.result
    }
    reader.readAsDataURL(file) // 读取文件内容为DataURL以便预览
    loading.value = true
    empty.value = false
    cropList.value = []
    result.value = {}
    selectItem.value = {}
    try {
      const data = new FormData()
      data.append('client_files[0]', file)
      data.append('algorithm_id', route.query.id)
      let res = await interfaceType(data)
      track()
      fileInput.value.value = ''
      value.value = ''
      result.value = res
      if (res.code === 0) {
        originalShow.value = false
        if (!res.shapes || res.shapes?.length === 0) {
          activeImage.value = ''
          empty.value = true
          const image = document.getElementById('previewImg')
          if (image) image.onload = null
          const el = canvasRef.value.getContext('2d')
          el.clearRect(0, 0, canvasRef.value.width, canvasRef.value.height)
        } else {
          if (res.shapes.length >= 1) {
            await drawImage(res, true)
          }
        }
      }
      if (res.code === 429) {
        empty.value = true
        often.value = true
        activeImage.value = ''
        originalShow.value = false
        const el = canvasRef.value.getContext('2d')
        el.clearRect(0, 0, canvasRef.value.width, canvasRef.value.height)
      }
      if (res.code === 1) {
        empty.value = true
        serverBusy.value = true
        activeImage.value = ''
        originalShow.value = false
        const el = canvasRef.value.getContext('2d')
        el.clearRect(0, 0, canvasRef.value.width, canvasRef.value.height)
      }

      loading.value = false
    } catch (err) {
      message.error(err.response?.data?.msg || '识别失败')
      originalShow.value = false
      activeImage.value = ''
      empty.value = true
      loading.value = false
      cropList.value = []
      selectItem.value = {}
      fileInput.value.value = ''
      const image = document.getElementById('previewImg')
      if (image) image.onload = null
      const el = canvasRef.value.getContext('2d')
      el.clearRect(0, 0, canvasRef.value.width, canvasRef.value.height)
    }
  } else {
    activeImage.value = null // 没有文件时清除预览图片
  }
}

const getCode = async () => {
  if (!callable.value) {
    message.warning('今天的试用次数已用完，请联系咨询', 4)
    isNeedVerify.value = false
    return
  }
  let res = await getAuthCode()
  if (res) captcha.value = res
}

const getVerify = async () => {
  if (!hasLoginSession()) return
  let res = await getNeed()
  isNeedVerify.value = res.need
  callable.value = res.callable
}
</script>

<template>
  <div class="section recognition-experience">
    <div class="content">
      <h2 class="title">功能体验</h2>
      <div class="content">
        <div class="image-preview">
          <div class="preview-left">
            <div class="preview-img">
              <div v-if="loading" class="loading"><a-spin size="large" /></div>
              <img id="previewImg" :src="activeImage" class="previewImg" />
              <canvas
                id="canvas"
                ref="canvasRef"
                class="canvas"
                :width="canvasW"
                :height="canvasH"
              />
              <div v-if="empty" class="empty">
                <EmptyItem
                  :formatCheck="formatCheck"
                  :sizeCheck="sizeCheck"
                  :often="often"
                  :routeName="routeName"
                  :serverBusy="serverBusy"
                />
              </div>
            </div>
            <div class="slick-thumb">
              <li
                v-for="(item, idx) in exampleImages"
                :key="idx"
                @click="handleActive(item, idx)"
                :class="[activeImage === item && 'active']"
              >
                <img :src="item" />
              </li>
              <div class="image-upload-action">
                <a-popover
                  v-model:open="visible"
                  title=""
                  :trigger="[]"
                  style="width: 357px"
                >
                  <template #content>
                    <div class="row">
                      <div class="row-item">
                        <span class="item">验证码：</span>
                        <a-input
                          v-model:value="value"
                          placeholder=""
                          style="width: 170px"
                          @change="handleChange"
                        />
                        <img
                          v-show="captcha.captcha_image_url"
                          :src="`${commonUrl}${captcha.captcha_image_url}`"
                          class="verify-img"
                        />
                        <span class="item vague" @click="getCode"
                          >刷新验证码</span
                        >
                      </div>
                      <div class="row-tips">
                        <span v-show="!nullShow && !errorShow"
                          >请输入验证码</span
                        >
                        <span v-show="nullShow" class="error"
                          >验证码是必填项</span
                        >
                        <span v-show="errorShow" class="error">{{
                          errorMsg
                        }}</span>
                      </div>
                      <div style="text-align: center; margin-top: 10px">
                        <a-button type="primary" @click="handleConfirm"
                          >确认</a-button
                        >
                      </div>
                    </div>
                  </template>
                  <a-button class="image-upload-button" @click="triggerFileInput">
                    <template #icon>
                      <CloudUploadOutlined />
                    </template>
                    上传图片
                  </a-button>
                </a-popover>
                <input
                  type="file"
                  ref="fileInput"
                  style="display: none"
                  @change="handleFileChange"
                  accept="image/*"
                />
              </div>
            </div>
          </div>
          <div class="preview-right">
            <div class="right-title">分析结果</div>
            <div class="slide-container" v-if="!OcrType">
              <div
                v-if="originalShow"
                :class="['img-border', { 'img-border-active': active === -1 }]"
              >
                <img
                  class="img"
                  :src="originalImg"
                  @click="
                    showLeftImage(
                      {
                        url: originalImg,
                      },
                      -1,
                    )
                  "
                />
              </div>
              <div
                :class="['img-border', { 'img-border-active': active === idx }]"
                v-for="(i, idx) in cropList"
                :key="i.url"
              >
                <img class="img" :src="i.url" @click="showLeftImage(i, idx)" />
              </div>
            </div>
            <div class="right-dec">
              <p v-show="selectItem.label !== undefined">
                <RightDec
                  :selectItem="selectItem"
                  :routeName="routeName"
                  :curData="curData"
                />
              </p>
            </div>
            <div v-show="cropList.length" class="bottom-tip">
              <CropText
                :source="source"
                :routeSource="routeSource"
                :originalShow="originalShow"
                :cropList="cropList"
              />
            </div>
          </div>
        </div>
      </div>

      <Tips :source="source" />
    </div>
  </div>

  <Indicator />
  <IntelligentModule />

  <div v-if="curData.application || application">
    <Application :list="curData.application || application" />
  </div>
</template>

<style lang="scss" scoped>
li {
  list-style: none;
}
.mb10 {
  margin-bottom: 0;
  height: 27px;
}
.ml0 {
  margin-left: 0 !important;
  margin-right: 5px;
}
.flex {
  display: flex;
}

.section {
  background-color: #fff;
  padding: 96px 0;

  .content {
    width: 1181px;
    margin: auto;

    .title {
      font-size: 30px;
      font-weight: 500;
      color: #000000;
      text-align: center;
      margin-bottom: 70px;
    }

    .image-preview {
      margin-top: 50px;
      display: flex;
      justify-content: space-between;
      align-items: stretch;

      .preview-left {
        background: #ffffff;
        display: flex;
        flex-direction: column;
        width: 585px;
        position: relative;
        margin-right: 30px;

      }

      .preview-right {
        background: #f7f7f7;
        width: 566px;
        position: relative;
        padding: 26px 43px 26px 45px;

        .right-title {
          font-weight: 500;
          font-size: 30px;
          color: #000000;
          text-align: left;
          margin-bottom: 30px;
          -moz-osx-font-smoothing: grayscale;
        }

        .slide-container {
          min-height: 97px;
          display: flex;
          overflow-x: auto;
          overflow-y: hidden;

          .img-border {
            width: auto;
            height: 89px;
            margin-right: 8px;
            cursor: pointer;
            border: 2px solid transparent;
            box-sizing: border-box;
            position: relative;
            img {
              width: auto;
              height: 85px;
              object-fit: fill;
            }
            &:last-child {
              margin-right: 0;
            }
          }

          .img-border-active {
            border-color: #ed000c;
            &::after {
              content: '';
              position: absolute;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              background-color: rgba(237, 0, 12, 0.15);
              pointer-events: none;
            }
          }
        }
      }

      .preview-img {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 585px;
        height: 370px;
        position: relative;
        background: #f7f7f7;

        img {
          width: 100%;
          max-height: 100%;
          position: relative;
        }

        .canvas,
        .empty {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
        }

        .loading {
          width: 585px;
          height: 370px;
          background-color: rgba(0, 0, 0, 0.2);
          position: absolute;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 500;
        }
      }
    }

    .previewImg {
      display: none;
    }
  }

  .tips {
    margin-top: 24px;
    color: #666;
  }

  .tips2 {
    font-size: 12px;
  }

  .bottom-tip {
    position: absolute;
    bottom: 26px;
    text-align: left;
    color: #666666;
  }
}

.row {
  padding: 15px 20px;
  .verify-img {
    width: 75px;
    height: 30px;
    margin-left: -4px;
    border: 1px solid rgba(0, 0, 0, 0.2);
    border-radius: 0 6px 6px 0;
    position: relative;
    z-index: 1001;
  }
}

.image-upload-action {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}
.image-upload-button {
  width: 132px;
  height: 44px;
  border: 1px solid var(--detail-line, #dce2ea);
  border-radius: 4px;
  background: #fff;
  color: var(--detail-text, #14233b);
  box-shadow: none;
  &:hover {
    color: #ed000c;
    border-color: #ed000c;
  }
}

.slick-thumb {
  margin-top: 40px;
  display: flex;
  li {
    width: 136px;
    height: 86px;
    cursor: pointer;
    margin-right: 13px;

    img {
      width: 100%;
      height: 100%;
      filter: grayscale(100%);
      display: block;
    }
  }

  .active {
    position: relative;
    box-shadow: 0 0 0 2px #5e87e8;
  }
}

.right-dec {
  margin-top: 10px;
}

.empty-item {
  color: #666;
  font-size: 16px;
  p {
    text-wrap: nowrap;
  }
}

.attribute {
  min-width: 140px;
  display: inline-block;
}

:deep() {
  .slick-dots {
    position: relative;
    height: auto;
  }

  .slick-slide img {
    border: 5px solid #fff;
    display: block;
    margin: auto;
    max-width: 80%;
  }

  slick-thumb li.slick-active img {
    filter: grayscale(0%);
  }

  .slick-thumb li img {
    filter: grayscale(0%);
  }

  .row-item {
    display: flex;
    align-items: center;
    .item {
      white-space: nowrap;
    }

    .vague {
      font-size: 12px;
      color: #ff0000;
      margin-left: 12px;
      cursor: pointer;
    }
  }

  .row-tips {
    height: 20px;
    font-size: 10px;
    line-height: 1.7;
    text-align: left;
    color: #999999;
    width: 100%;
    padding-left: 57px;

    .error {
      color: #ff4d4f;
    }
  }

  .ant-popover .ant-popover-inner {
    padding: 30px !important;
  }

  .ant-btn-primary {
    background: #220079;
    &:hover {
      background: #220079;
    }
  }
}
</style>
